import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { renderMath, renderMathRTLSafe, validateLatex } from '../utils/mathUtils';

interface MathRendererProps {
  expression: string;
  displayMode?: boolean;
  showCopyButton?: boolean;
  equationNumber?: number;
  id?: string;
  showSourceToggle?: boolean;
}

const MathRenderer: React.FC<MathRendererProps> = ({
  expression,
  displayMode = false,
  showCopyButton = true,
  equationNumber,
  id,
  showSourceToggle
}) => {
  const [copied, setCopied] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const sourceToggleEnabled = showSourceToggle ?? (process.env.NODE_ENV === 'development');

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(expression);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const validation = validateLatex(expression);
  
  if (!validation.valid) {
    return (
      <div className="math-error bg-red-50 border border-red-200 rounded-lg p-3 my-2">
        <div className="flex items-center justify-between">
          <span className="text-red-700 font-medium">Math Rendering Error</span>
          <button
            onClick={() => setShowSource(!showSource)}
            className="text-red-600 hover:text-red-800 text-sm underline"
          >
            {showSource ? 'Hide' : 'Show'} Source
          </button>
        </div>
        {showSource && (
          <pre className="mt-2 text-sm text-red-600 bg-red-100 p-2 rounded overflow-x-auto">
            {expression}
          </pre>
        )}
        <p className="text-red-600 text-sm mt-1">{validation.error}</p>
      </div>
    );
  }

  const renderedMath = renderMathRTLSafe(expression, displayMode);

  return (
    <div 
      className={`math-container group relative ${displayMode ? 'block my-4' : 'inline'}`}
      id={id}
      style={{
        direction: 'ltr',
        unicodeBidi: 'isolate'
      }}
    >
      {/* Equation number for display math */}
      {displayMode && equationNumber && (
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 text-sm text-secondary-500">
          ({equationNumber})
        </div>
      )}
      
      {/* Rendered math - wrapped in bdi element for maximum RTL isolation */}
      <bdi 
        className={`math-content ${displayMode ? 'text-center' : 'inline'}`}
        dir="ltr"
        style={{ 
          direction: 'ltr',
          unicodeBidi: 'isolate',
          display: displayMode ? 'block' : 'inline-block'
        }}
        dangerouslySetInnerHTML={{ 
          __html: renderedMath // Already RTL-safe from renderMathRTLSafe
        }}
      />
      
      {/* Copy button - only show on hover for display math */}
      {showCopyButton && displayMode && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={copyToClipboard}
            className="bg-white hover:bg-secondary-50 border border-secondary-300 rounded p-1.5 shadow-sm transition-colors"
            title="Copy LaTeX source"
          >
            {copied ? (
              <Check className="w-3 h-3 text-green-600" />
            ) : (
              <Copy className="w-3 h-3 text-secondary-600" />
            )}
          </button>
        </div>
      )}
      
      {/* Source toggle for debugging */}
      {sourceToggleEnabled && (
        <button
          onClick={() => setShowSource(!showSource)}
          className="ml-2 text-xs text-secondary-500 hover:text-secondary-700 underline"
        >
          src
        </button>
      )}
      
      {/* Source code display */}
      {sourceToggleEnabled && showSource && (
        <div className="mt-2 p-2 bg-secondary-100 rounded text-xs font-mono overflow-x-auto">
          {expression}
        </div>
      )}
    </div>
  );
};

export default MathRenderer;

// Enhanced Block Math Component
interface BlockMathProps {
  children: string;
  equationNumber?: number;
  id?: string;
  className?: string;
}

export const BlockMath: React.FC<BlockMathProps> = ({ 
  children, 
  equationNumber, 
  id, 
  className = '' 
}) => {
  return (
    <div className={`block-math ${className}`}>
      <MathRenderer
        expression={children}
        displayMode={true}
        equationNumber={equationNumber}
        id={id}
      />
    </div>
  );
};

// Enhanced Inline Math Component
interface InlineMathProps {
  children: string;
  className?: string;
}

export const InlineMath: React.FC<InlineMathProps> = ({ 
  children, 
  className = '' 
}) => {
  return (
    <span className={`inline-math ${className}`}>
      <MathRenderer
        expression={children}
        displayMode={false}
        showCopyButton={false}
        showSourceToggle={false}
      />
    </span>
  );
};
